import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
	Alert,
	Button,
	Divider,
	Drawer,
	Flex,
	Input,
	Modal,
	Segmented,
	Select,
	Table,
	Typography,
} from 'antd';
import { LuFileDown, LuSave, LuSettings, LuTrash2 } from 'react-icons/lu';

import { useProject } from '@contexts/Project.context';
import { useCollectionStore } from '@stores/useCollectionStore';
import { formatArea } from '@utils/measurementSystem';
import { formatNumber } from '@utils/helpers';
import {
	describeCoverageGaps,
	getSimpleFilterCoverageGaps,
	getMatrixFilterCoverageGaps,
} from '@routes/userRoutes/projects/singleProject/components/modifyLayers/hexagonColorFilter/matrixFilter/matrixClassification';
import useCurrencies from '@routes/userRoutes/projects/singleProject/components/modifyLayers/hexagonColorFilter/matrixFilter/useCurrencies';

import SidebarTemplate from '../../../../sidebars/sidebarElements/SidebarTemplate';
import { COLLECTION_SIDEBAR_IDS, DEFAULT_CURRENCY } from '../../constants';
import {
	getAvailableProperties,
	getPropertyRange,
	getPerBandThresholdRanges,
} from '../../utils/hexagonClassificationProperties';
import { collectionHexagonsSummary } from '../../utils/collectionHexagonsSummary';
import useHexagonFilterTemplates from '../../hooks/useHexagonFilterTemplates';
import useHexagonExport from '../../hooks/useHexagonExport';
import SimpleEditor from './SimpleEditor';
import MatrixEditor from './MatrixEditor';
import CostSummary from './CostSummary';
import FilterEditorHeader from './FilterEditorHeader';
import CollectionExportReportModal from './CollectionExportReportModal';
import ClassLabelCell from './ClassLabelCell';

const { Text } = Typography;

const MODE_OPTIONS = [
	{ value: 'matrix', label: 'Matrix (2D)' },
	{ value: 'simple', label: 'Simple (1D)' },
];

// The two non-user buckets summaryRows appends beneath the user classes.
const SPECIAL_CLASS_IDS = new Set(['below_minimum', 'unclassified']);

// Classification Results table columns — no dependencies, so a plain constant
// rather than a memoized value.
const RESULTS_COLUMNS = [
	{
		title: 'Class',
		dataIndex: 'label',
		key: 'label',
		render: (label, row) => <ClassLabelCell label={label} color={row.color} />,
	},
	{
		title: 'Cell',
		dataIndex: 'count',
		key: 'count',
		align: 'left',
		render: count => formatNumber(count),
	},
	{
		// The detected object area Cost Summary prices, beside the cell area the
		// filter classifies on. The below_minimum/unclassified rows carry a real
		// object area but are never priced (see 3.8), so they show a dash rather
		// than a value — which makes this column total exactly the Cost Summary's
		// "Total object area". Base unit, like the "Area" column beside it.
		title: 'Object area',
		dataIndex: 'objectArea',
		key: 'objectArea',
		align: 'left',
		render: (objectArea, row) => {
			if (SPECIAL_CLASS_IDS.has(row.id)) return '–';
			const [areaNum, areaUnit] = formatArea(objectArea, 1, false);
			return `${areaNum} ${areaUnit}`;
		},
	},
	{
		title: 'Cell area',
		dataIndex: 'area',
		key: 'area',
		align: 'left',
		render: area => {
			const [areaNum, areaUnit] = formatArea(area, 1, false);
			return `${areaNum} ${areaUnit}`;
		},
	},

	{
		// Share of the total cell area, unclassified cells included (see
		// useHexagonClassification's `percentageBasis`) — not of the count beside
		// it. Named for the same reason as the DSP table's "% of plots".
		title: '% of cell area',
		dataIndex: 'sharePercentage',
		key: 'sharePercentage',
		align: 'left',
		render: pct => `${pct}%`,
	},
];

/**
 * Color-filter sidebar for collection hexagons: classifies cells by a matrix
 * (2D) or simple (1D) filter and colors them by class. The active filter config
 * lives in the store (`hexagonClassificationFilter`) so edits preview live on
 * the map; `null` = heat mode.
 *
 * Closing the panel deliberately keeps that config: CollectionHexagonLayer
 * gates classified coloring on this sidebar being open, so the map returns to
 * heat on close by itself, and reopening restores the user's filter and
 * template rather than resetting them to a default.
 *
 * This component owns only classification-derived display data (currency,
 * observed ranges, coverage gaps, results-table rows) and UI composition.
 * Filter-mode/template CRUD and report/geometry export are independent
 * concerns pulled out into useHexagonFilterTemplates and useHexagonExport —
 * neither touches the other's state, they just both consume `filter`/`results`.
 *
 * Rebuilt in antd rather than reusing the Bootstrap model editors.
 *
 * @param {object} props
 * @param {import('../../utils/collectionHexagonsToCells').HexagonCell[]} props.cells
 * @param {Record<string, string>} props.classByCellId - per-cell class id, from
 *   the parent layer's useHexagonClassification call (shared, not recomputed
 *   here — the layer needs the same classification for map styling).
 * @param {object} props.results - per-class classification results, same source.
 * @param {Array<{key: string, label: string}>} props.objects - collection detection objects,
 *   listed as the report's detection classes.
 */
const CollectionHexagonColorFilter = ({
	cells,
	classByCellId,
	results,
	collectionName,
	objects,
}) => {
	const { modifyLayersSidebarRef } = useProject();

	const filter = useCollectionStore(s => s.hexagonClassificationFilter);
	const setFilter = useCollectionStore(s => s.setHexagonClassificationFilter);

	const [projectNotes, setProjectNotes] = useState('');
	const [showEditor, setShowEditor] = useState(false);

	const resolution = useCollectionStore(s => s.hexagonResolution);

	const currencies = useCurrencies();

	const availableProperties = useMemo(
		() => getAvailableProperties(cells, objects),
		[cells, objects]
	);
	const availableIds = useMemo(
		() => new Set(availableProperties.map(p => p.id)),
		[availableProperties]
	);

	const {
		activeTemplate,
		showSaveModal,
		setShowSaveModal,
		saveTitle,
		setSaveTitle,
		saveTemplate,
		saveMutation,
		deleteMutation,
		templateOptions,
		selectMode,
		handleTemplateSelect,
		handleDeleteTemplate,
	} = useHexagonFilterTemplates(filter, setFilter, resolution, availableIds);

	const isSimple = filter?.type === 'simple';

	// Resolved currency object for price labels/report — falls back to the
	// first entry (UNIT) like the model path, then to the shared default if
	// currencies is ever empty (matches the fallback the child editors use).
	const currency =
		currencies.find(c => c.id === filter?.currency) || currencies[0] || DEFAULT_CURRENCY;

	// Whole-collection detection summary (all classes, not just the active
	// color-filter's), used for the exported report's detection stats table.
	const summary = useMemo(() => collectionHexagonsSummary(cells), [cells]);

	// Merge partial updates into the active filter, reading the latest from the
	// store to avoid stale-closure merges during rapid edits.
	const updateFilter = useCallback(
		updates => {
			const current = useCollectionStore.getState().hexagonClassificationFilter ?? {};
			setFilter({ ...current, ...updates });
		},
		[setFilter]
	);

	// Observed data range for the active simple property — used for the
	// editor's range sliders/hint text (mirrors the model's usePropertyRanges).
	const propertyRange = useMemo(
		() => (isSimple ? getPropertyRange(cells, filter?.property) : null),
		[isSimple, cells, filter?.property]
	);

	// Per-band observed threshold ranges — feeds the matrix coverage-gap check
	// below (getMatrixFilterCoverageGaps); MatrixEditor doesn't consume this.
	const perBandThresholdRanges = useMemo(
		() =>
			!isSimple && filter
				? getPerBandThresholdRanges(
						cells,
						filter.bandProperty,
						filter.thresholdProperty,
						filter.bands
					)
				: [],
		[isSimple, filter, cells]
	);

	// Coverage gaps — reuse the pure detectors with collection-native ranges.
	// Computed once here so the same result drives both the editor's warning
	// AND the exported report (see useHexagonExport), rather than each
	// recomputing it separately.
	const coverageGaps = useMemo(() => {
		if (!filter) return null;
		return isSimple
			? getSimpleFilterCoverageGaps(filter.classes, propertyRange)
			: getMatrixFilterCoverageGaps(filter, perBandThresholdRanges);
	}, [filter, isSimple, propertyRange, perBandThresholdRanges]);

	const coverageMessage = useMemo(
		() => describeCoverageGaps(filter, coverageGaps),
		[filter, coverageGaps]
	);

	// Results rows: user classes (even at 0) then the special buckets when non-empty.
	// Matches the model path's Classification Results table (MatrixFilterMode.jsx):
	// every configured class always shows, below_minimum/unclassified only when
	// they actually have matches.
	const summaryRows = useMemo(() => {
		if (!filter) return [];
		const classRows = (filter.classes ?? []).map(c => results[c.id]).filter(Boolean);
		const specials = ['below_minimum', 'unclassified']
			.map(id => results[id])
			.filter(r => r?.count > 0);
		return [...classRows, ...specials];
	}, [filter, results]);

	const {
		showExportModal,
		setShowExportModal,
		exportTheme,
		setExportTheme,
		isExporting,
		handleExport,
		exportGeometry,
	} = useHexagonExport({
		cells,
		classByCellId,
		results,
		filter,
		collectionName,
		objects,
		resolution,
		projectNotes,
		currency,
		availableProperties,
		coverageGaps,
		summary,
	});

	if (!modifyLayersSidebarRef?.current) return null;

	return createPortal(
		<SidebarTemplate
			zindex="2"
			sidebarId={COLLECTION_SIDEBAR_IDS.hexagonColorFilter}
			header="Hexagon Classification & Price Estimation"
			width="wide"
			closeable>
			<Flex vertical gap={12} style={{ padding: '12px 16px' }}>
				{/* Cell size, read-only. It is a saved collection setting and lives in
				    the Hexagon Settings panel with the colour; it is named here because
				    every area and price below is per cell, so the number they are built
				    on should be visible without leaving the panel. */}
				<Flex align="center" justify="space-between" gap={8}>
					<Text style={{ fontSize: '0.8rem' }}>Cell size (m²)</Text>
					<Text type="secondary" style={{ fontSize: '0.8rem' }}>
						{formatNumber(resolution)} · change in Hexagon Settings
					</Text>
				</Flex>

				<Divider style={{ margin: 0 }} />

				{/* Filter mode */}
				<Flex vertical gap={4}>
					<Text style={{ fontSize: '0.8rem' }}>Filter mode</Text>
					<Segmented
						block
						options={MODE_OPTIONS}
						value={filter?.type}
						onChange={selectMode}
					/>
				</Flex>

				{!filter && (
					<Text type="secondary" style={{ fontSize: '0.8rem' }}>
						Pick a mode to color hexagons by class, or load a saved template.
					</Text>
				)}

				{/* Filter template */}
				<Flex vertical gap={4}>
					<Text style={{ fontSize: '0.8rem' }}>Filter template</Text>
					{/* Default (middle) size to match the Save/Update/Delete buttons below —
					    size="small" made the select noticeably shorter than the buttons. */}
					<Select
						placeholder="-- Select template --"
						value={activeTemplate?.uuid}
						onChange={handleTemplateSelect}
						options={templateOptions}
						allowClear
						onClear={() => selectMode(filter?.type === 'simple' ? 'simple' : 'matrix')}
						notFoundContent="No saved templates"
					/>
					<Flex gap={8}>
						<Button
							icon={<LuSave />}
							onClick={() => {
								setSaveTitle(filter?.name ?? '');
								setShowSaveModal(true);
							}}
							disabled={!filter}
							style={{ flex: 1 }}>
							{activeTemplate ? 'Update template' : 'Save as template'}
						</Button>
						{activeTemplate && (
							<Button
								danger
								icon={<LuTrash2 />}
								loading={deleteMutation.isPending}
								onClick={handleDeleteTemplate}>
								Delete
							</Button>
						)}
					</Flex>
				</Flex>

				{filter && (
					<>
						<Divider style={{ margin: 0 }} />

						{/* Classification Results — mirrors the model path's table */}
						<Flex vertical gap={4}>
							<Text
								type="secondary"
								style={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>
								Classification Results
							</Text>
							<Table
								size="small"
								pagination={false}
								columns={RESULTS_COLUMNS}
								dataSource={summaryRows}
								rowKey="id"
							/>
						</Flex>

						{coverageMessage && (
							<Alert
								type="warning"
								showIcon
								message={coverageMessage}
								style={{ fontSize: '0.75rem' }}
							/>
						)}

						{/* Edit filter — opens the full editor (properties, classes, pricing,
						    notes) in a bottom drawer, matching the model path's Offcanvas. */}
						<Button icon={<LuSettings />} onClick={() => setShowEditor(true)} block>
							Edit filter
						</Button>

						<Divider style={{ margin: 0 }} />

						{/* Export — mirrors the model path's KML/GeoJSON/Export Report row.
						    "Export Report" also lives in the editor drawer's right column
						    (next to the cost/notes it summarizes) — same dual placement the
						    model path uses (HexagonColorFilter's row + MatrixFilterMode's
						    own Export Report button inside the editor). */}
						<Flex gap={6}>
							<Button
								style={{
									flex: 1,
									minWidth: 0,
									padding: '0 6px',
									fontSize: '0.75rem',
								}}
								onClick={() => exportGeometry('kml')}>
								Export KML
							</Button>
							<Button
								style={{
									flex: 1,
									minWidth: 0,
									padding: '0 6px',
									fontSize: '0.75rem',
								}}
								onClick={() => exportGeometry('geojson')}>
								Export GeoJSON
							</Button>
							<Button
								style={{
									flex: 1,
									minWidth: 0,
									padding: '0 6px',
									fontSize: '0.75rem',
								}}
								icon={<LuFileDown />}
								onClick={() => setShowExportModal(true)}>
								Export Report
							</Button>
						</Flex>
					</>
				)}
			</Flex>

			{/* Filter editor drawer — properties/classes on the left, cost/notes/export
			    on the right. Bottom placement mirrors the model path's Offcanvas
			    editor; capped height + no mask (mirrors the Offcanvas's
			    maxHeight:50vh and backdrop={false}) so the map stays visible and
			    interactive behind it instead of a near-fullscreen drawer with a
			    dimming overlay. */}
			<Drawer
				title={
					<FilterEditorHeader
						isSimple={isSimple}
						filter={filter}
						updateFilter={updateFilter}
						availableProperties={availableProperties}
					/>
				}
				placement="bottom"
				height="50vh"
				mask={false}
				open={showEditor}
				onClose={() => setShowEditor(false)}
				destroyOnHidden
				styles={{ body: { overflowY: 'auto' } }}>
				{filter && (
					<Flex gap={24} align="flex-start" wrap="wrap">
						{/* Left: classification config (property/class/threshold editing) */}
						<Flex vertical style={{ flex: 2, minWidth: 320 }}>
							{isSimple ? (
								<SimpleEditor
									filter={filter}
									updateFilter={updateFilter}
									availableProperties={availableProperties}
									classificationResults={results}
									propertyRange={propertyRange}
									coverageMessage={coverageMessage}
								/>
							) : (
								<MatrixEditor
									filter={filter}
									updateFilter={updateFilter}
									availableProperties={availableProperties}
									coverageMessage={coverageMessage}
								/>
							)}
						</Flex>

						{/* Right: cost summary → notes → export, in that order. Sits beside
						    the threshold grid so ranges and their cost read side by side;
						    min width covers the class rows' edit/remove controls. */}
						<Flex vertical gap={16} style={{ flex: 1, minWidth: 300 }}>
							<CostSummary
								filter={filter}
								updateFilter={updateFilter}
								classificationResults={results}
								currency={currency}
								currencies={currencies}
							/>

							<Divider style={{ margin: 0 }} />

							{/* Project notes — included verbatim in the exported report. */}
							<Flex vertical gap={4}>
								<Text style={{ fontSize: '0.8rem' }}>Project notes (optional)</Text>
								<Input.TextArea
									rows={3}
									placeholder="Add notes about the project, calculations, or observations..."
									value={projectNotes}
									onChange={e => setProjectNotes(e.target.value)}
									style={{ fontSize: '0.85rem' }}
								/>
								<Text type="secondary" style={{ fontSize: '0.75rem' }}>
									Notes will be included in exported reports.
								</Text>
							</Flex>

							<Button
								type="primary"
								icon={<LuFileDown />}
								onClick={() => setShowExportModal(true)}
								block>
								Export Report
							</Button>
						</Flex>
					</Flex>
				)}
			</Drawer>

			{/* Save-template modal */}
			<Modal
				title={activeTemplate ? 'Update template' : 'Save template'}
				open={showSaveModal}
				onOk={() => saveTemplate({ title: saveTitle })}
				onCancel={() => setShowSaveModal(false)}
				okButtonProps={{ loading: saveMutation.isPending, disabled: !saveTitle.trim() }}
				okText="Save">
				<Input
					placeholder="Template name"
					value={saveTitle}
					onChange={e => setSaveTitle(e.target.value)}
					onPressEnter={() => saveTitle.trim() && saveTemplate({ title: saveTitle })}
				/>
			</Modal>

			{/* Export options modal */}
			<CollectionExportReportModal
				open={showExportModal}
				onClose={() => setShowExportModal(false)}
				exportTheme={exportTheme}
				setExportTheme={setExportTheme}
				onExport={handleExport}
				isExporting={isExporting}
			/>
		</SidebarTemplate>,
		modifyLayersSidebarRef.current
	);
};

export default CollectionHexagonColorFilter;
